/**
 * useAccountSetupRedirect — send a member who never finished setup to `/account-setup`.
 *
 * Two surfaces hit the same wall and must behave identically: the sign-in modal, and
 * the password-reset page's auto-login. Both catch `AccountSetupRequiredError` and call
 * this.
 *
 * Why a countdown toast rather than an immediate redirect: the sign-in modal can be
 * mounted inside an in-progress booking or subscription flow, and navigating away
 * unmounts it. The countdown gives the member a "Stay here" escape before that happens,
 * while still moving them along on its own if they do nothing.
 *
 * @module services/auth/hooks
 */

import { useCallback } from 'react';
import { butterToast } from '@butter/ui/patterns';
import { useTenantNavigate, useTenantSearchParams } from '~/lib/navigation';
import { buildUrl, QueryParams } from '~/lib/queryParams';
import type { AccountSetupRequiredError } from '../api/authService';

export function useAccountSetupRedirect() {
  const navigate = useTenantNavigate();
  const [searchParams] = useTenantSearchParams();

  return useCallback(
    (err: AccountSetupRequiredError) => {
      // `buildUrl` drops null/undefined values, and explicit params beat preserved
      // ones. Prefer the API's home club; fall back to the club we're already on, since
      // MissingParamsGuard refuses to render /account-setup without club-id AND
      // tenant-handle. The handle always comes from the current URL — a same-tenant
      // login has no reason to return one.
      const url = buildUrl('/account-setup', {
        [QueryParams.USER_ID]: err.userId,
        [QueryParams.CLUB_ID]: err.clubId ?? searchParams.get(QueryParams.CLUB_ID),
        [QueryParams.TENANT_HANDLE]: searchParams.get(QueryParams.TENANT_HANDLE),
      });

      butterToast.countdown({
        // Stable id — a member who submits twice gets one toast, not a stack.
        id: 'account-setup-required',
        title: 'Account setup required',
        message: err.invited
          ? "We've re-sent your setup email. Taking you to the setup screen…"
          : 'Taking you to the setup screen…',
        seconds: 5,
        actionLabel: 'Go now',
        cancelLabel: 'Stay here',
        onComplete: () => navigate(url),
      });
    },
    [navigate, searchParams],
  );
}
